/**
 * downloadFile — Trigger a browser download for a blob or text payload.
 *
 * Three demos (Governed-K graph export, Hydra Cut exports, and demos/app's
 * media export) each had their own slight variant of this.
 * Variants drifted on: filename sanitization (or its absence), blob URL
 * lifetime (synchronous vs deferred revoke), and DOM hygiene
 * (`document.body.appendChild` vs not). This module is the single
 * audited implementation; consumers should not roll their own.
 *
 * Security: the filename is sanitized by construction — only safe
 * characters survive. Path separators, parent-directory traversal
 * sequences, and control characters are structurally inexpressible in
 * the result. The blob URL revoke is deferred so large downloads do
 * not race a fast revoke and abort mid-transfer.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

/** How long the blob URL stays alive before revocation. */
const BLOB_URL_REVOKE_DELAY_MS = 60_000;

/**
 * Sanitize a filename to a safe ASCII subset. Keeps alphanumerics,
 * underscore, dash, dot, and space. Collapses consecutive dots so
 * `foo..bar` becomes `foo.bar` (the `..` traversal sequence is
 * structurally inexpressible in the result).
 *
 * The leading/trailing trim is a single linear scan rather than the
 * alternation `^[_.\s]+|[_.\s]+$`; the two-anchor alternation form
 * admits polynomial backtracking on adversarial input.
 */
export function sanitizeDownloadFilename(name: string): string {
  const collapsed = name.replace(/[^a-zA-Z0-9._\- ]/g, "_").replace(/\.{2,}/g, ".");
  let start = 0;
  let end = collapsed.length;
  while (start < end && isTrimmableFilenameChar(collapsed.charCodeAt(start))) start += 1;
  while (end > start && isTrimmableFilenameChar(collapsed.charCodeAt(end - 1))) end -= 1;
  const cleaned = start === 0 && end === collapsed.length ? collapsed : collapsed.slice(start, end);
  return cleaned.length > 0 ? cleaned : "download";
}

function isTrimmableFilenameChar(code: number): boolean {
  return (
    code === 0x5f /* _ */ ||
    code === 0x2e /* . */ ||
    code === 0x20 /* space */ ||
    code === 0x09 /* tab */ ||
    code === 0x0a /* LF */ ||
    code === 0x0b /* VT */ ||
    code === 0x0c /* FF */ ||
    code === 0x0d /* CR */
  );
}

/**
 * Trigger a browser download for the given blob with the given name.
 * The blob's MIME type is taken from the blob itself; callers wanting
 * a specific MIME should construct the blob with `{ type }`.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  if (typeof document === "undefined" || typeof URL === "undefined") {
    throw new Error("downloadBlob requires a browser environment");
  }
  const safe = sanitizeDownloadFilename(filename);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = safe;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, BLOB_URL_REVOKE_DELAY_MS);
}

/**
 * Trigger a browser download for a string of `content` under the
 * declared MIME type. Convenience wrapper over {@link downloadBlob}.
 */
export function downloadFile(filename: string, content: string, mimeType: string): void {
  downloadBlob(new Blob([content], { type: mimeType }), filename);
}

/**
 * Trigger a browser download for a JSON-serializable value, pretty-
 * printed with two-space indent. The filename gets the `.json`
 * suffix appended if it does not already have one.
 */
export function downloadJsonFile(filename: string, value: unknown): void {
  const name = filename.toLowerCase().endsWith(".json") ? filename : `${filename}.json`;
  downloadFile(name, JSON.stringify(value, null, 2), "application/json");
}

/** Read a browser File object and parse it as JSON. */
export async function readJsonFile(file: File): Promise<unknown> {
  const text = await file.text();
  return JSON.parse(text) as unknown;
}
